import React, { useRef, useState } from 'react'
import { Alert, Box, Button } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import useAccount from '../../hooks/useAccount'
import { pendingStepsForJob, useDemoJobInfo, useDemoRun } from '../../pages/admin/useDemos'
import type { DemoRun, DemoStepStatus } from '../../pages/admin/useDemos'
import DemoFunnelOverlay from './DemoFunnelOverlay'

export interface DemoStepButtonProps {
  customer: string
  job: string
  /** Refetch the page's data once the step's data has landed. */
  onLanded?: () => void
  /** The funnel's count, in ms (tests shorten nothing; the default is ~8s). */
  durationMs?: number
}

/**
 * A demo job's staged steps on the AutoCoder pipeline page (design/2026-10-22-
 * chief-of-staff-and-demos.md T20): one button per step that has not run
 * since the last Reset, for Managers only, and nothing at all on a job that
 * is not a demo job. A click starts the step on the server and shows the
 * story's funnel; when the count has finished AND the data has landed, the
 * page refetches and shows it.
 */
const DemoStepButton: React.FC<DemoStepButtonProps> = ({ customer, job, onLanded, durationMs }) => {
  const account = useAccount()
  const isManager = Boolean(account.user?.role_permissions?.isManager)
  const { info, refetchInfo } = useDemoJobInfo(isManager ? customer : undefined, isManager ? job : undefined)

  const [playing, setPlaying] = useState<DemoStepStatus | null>(null)
  const [animDone, setAnimDone] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  // Mirrors for the two callbacks that finish a step, which arrive in
  // either order and outside a render.
  const animDoneRef = useRef(false)
  const finishedRef = useRef<DemoRun | null>(null)

  const finish = () => {
    setPlaying(null)
    setAnimDone(false)
    setFailure(null)
    animDoneRef.current = false
    finishedRef.current = null
    void refetchInfo()
    onLanded?.()
  }

  // Whichever of the count and the run ends second lands the step.
  const settle = () => {
    const run = finishedRef.current
    if (!animDoneRef.current || !run) return
    if (run.status === 'succeeded') finish()
    else setFailure(run.error || 'The step failed.')
  }

  const runner = useDemoRun((run) => {
    finishedRef.current = run
    settle()
  })

  if (!isManager || !info?.is_demo || !info.story) return null
  const steps = pendingStepsForJob(info.steps, job)
  if (steps.length === 0 && !playing) return null

  const play = async (step: DemoStepStatus) => {
    animDoneRef.current = false
    finishedRef.current = null
    setAnimDone(false)
    setFailure(null)
    setPlaying(step)
    const runId = await runner.startStep(info.story as string, step.id)
    if (!runId) setPlaying(null)
  }

  return (
    <>
      <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
        {steps.map((step) => (
          <Button
            key={step.id}
            variant="contained"
            color="info"
            size="small"
            startIcon={<PlayArrowIcon />}
            disabled={Boolean(playing) || Boolean(info.running)}
            onClick={() => void play(step)}
          >
            {step.label}
          </Button>
        ))}
        {runner.startError && !playing && (
          <Alert severity="error" sx={{ py: 0 }}>{runner.startError}</Alert>
        )}
      </Box>
      {playing && (
        <DemoFunnelOverlay
          label={playing.label}
          funnel={playing.funnel}
          durationMs={durationMs}
          landing={animDone && !failure}
          error={failure ?? runner.runError}
          onAnimationEnd={() => {
            animDoneRef.current = true
            setAnimDone(true)
            settle()
          }}
          onClose={finish}
        />
      )}
    </>
  )
}

export default DemoStepButton
